import { fillPath } from './urlPath.js';

// Route that streams a stored object's own bytes — a forms answer file or a field attachment.
// `attachmentFileUrl` in packages/web/src/utils is the only thing that should build the full URL.
export const ATTACHMENT_FILE_ROUTE = '/fileSystemServer/getAttachmentFile';

export const SIGNUP_ROUTE = '/signup';
/** The web app's page where a person lets an assistant reach their CUBE account; /authorize opens it. */
export const CONNECT_ASSISTANT_ROUTE = '/connect-assistant';

export const DASHBOARD_ROUTE = '/dashboard';

export interface ReactRouterLocation {
  pathname: string;
  search: string;
  hash: string;
  state: unknown;
  key: string;
}

export interface AddRedirectParamOptions {
  targetPath: URL;
  redirectTo: Location | URL;
}

// Every member is `?: T | undefined` on purpose: under `exactOptionalPropertyTypes` the call sites
// build this bag from optional route params / state that are genuinely `T | undefined`, and the
// builder below already treats an absent key and an explicit `undefined` identically.
export interface GetViewerUrlParams {
  fileVersionId?: string | undefined;
  transmittalVersionId?: string | undefined;
  issueId?: string | undefined;
  estimateId?: string | undefined;
  measurementId?: string | undefined;
  versionId?: string | undefined;
  spaceId?: string | undefined;
  scheduleId?: string | undefined;
  // Optional: getViewerUrl returns early (undefined) when projectId is falsy (see guard below).
  projectId?: string | undefined;
  contractId?: string | undefined;
  preloadAndOpen?: boolean | undefined;
  mode?: "scs" | "skipProperties" | undefined;
  shortcutParentId?: string | undefined;
  clashTestId?: string | undefined;
  scheduleVersionId?: string | undefined;
  redirectTo?: URL | undefined;
  frontEndOrigin?: string | undefined;
  searchCondition?: string | undefined;
  searchValue?: string | undefined;
}

export const addRedirectParam = ({
  targetPath,
  redirectTo
}: AddRedirectParamOptions): string => {
  const targetPathStr = targetPath.pathname;
  const targetSearchParams = new URLSearchParams(targetPath.search);
  const targetHash = targetPath.hash;
  const currentPath = `${redirectTo.pathname}${redirectTo.search}${redirectTo.hash}`;
  targetSearchParams.set('redirectUrl', encodeURIComponent(currentPath));
  return `${targetPathStr}?${targetSearchParams.toString()}${targetHash}`;
};

export const extractRedirectUrl = (urlOrLocation: URL | ReactRouterLocation): URL => {
  const fallbackRoute = DASHBOARD_ROUTE;
  let searchParams: URLSearchParams;

  if ('searchParams' in urlOrLocation) {
    searchParams = urlOrLocation.searchParams;
  } else {
    searchParams = new URLSearchParams(urlOrLocation.search);
  }

  const redirectUrl = searchParams.get('redirectUrl');

  let origin;
  const isClient = typeof globalThis !== 'undefined' && globalThis.location;
  if (isClient) {
    origin = globalThis.location.origin;
  } else {
    origin = process.env.FRONTEND_SOCIAL_LOGIN_URL;
  }
  
  if (redirectUrl) {
    const decodedUrl = decodeURIComponent(redirectUrl);
    return new URL(decodedUrl, origin);
  }
  return new URL(fallbackRoute, origin);
};

export const extractRedirectUrlAsString = (urlOrLocation: URL | ReactRouterLocation): string => {
  const fallbackRoute = DASHBOARD_ROUTE;
  let searchParams: URLSearchParams;
  
  if ('searchParams' in urlOrLocation) {
    searchParams = urlOrLocation.searchParams;
  } else {
    searchParams = new URLSearchParams(urlOrLocation.search);
  }
  
  const redirectUrl = searchParams.get('redirectUrl');
  if (redirectUrl) {
    const decodedUrl = decodeURIComponent(redirectUrl);
    return decodedUrl;
  }
  return fallbackRoute;
};

export const getViewerUrl = ({
  fileVersionId, 
  transmittalVersionId, 
  issueId, 
  estimateId, 
  measurementId, 
  versionId, 
  spaceId, 
  scheduleId, 
  projectId, 
  contractId,
  preloadAndOpen,
  mode,
  shortcutParentId,
  clashTestId, 
  scheduleVersionId,
  redirectTo,
  frontEndOrigin,
  searchCondition,
  searchValue,
}: GetViewerUrlParams): string | undefined => {
  if (!projectId) {
    return;
  }
  // ⚠️ KNOWN BUG: an absent fileVersionId is written as "undefined", a viewer link that cannot open.
  const location = fillPath('/designviewer/file/:fileId', { fileId: String(fileVersionId) });
  const paramMappings: Record<string, string | undefined> = {
    scheduleId,
    projectId,
    spaceId,
    contractId,
    transmittalId: transmittalVersionId,
    shortcutParentId,
    issueId,
    estimateId,
    measurementId,
    versionId,
    clashTestId,
    scheduleVersionId,
    mode: mode ?? (preloadAndOpen ? 'scs' : undefined),
    searchCondition,
    searchValue,
  };
  const searchParams = new URLSearchParams();
  for(const [key, value] of Object.entries(paramMappings)) {
    if (value) {
      searchParams.set(key, value);
    }
  }
  const baseUrl = `${location}?${searchParams.toString()}`;
  const isClient = typeof globalThis !== 'undefined' && globalThis.location;
  const finalRedirectTo = redirectTo || (isClient ? globalThis.location : null);
  
  if (!finalRedirectTo) {
    return baseUrl;
  }
  
  const origin = isClient 
    ? globalThis.location.origin 
    : frontEndOrigin;

  return addRedirectParam({
    redirectTo: finalRedirectTo,
    targetPath: new URL(baseUrl, origin),
  });
};

export const getInspectionUrl = ({
  projectId, 
  resourceId, 
  resourceType, 
  contractId
}: any) => {
  // ⚠️ KNOWN BUG: an absent projectId / resourceId is written as "undefined" (stale recent-access records, form
  // notifications; see getRecentAccessUrl.ts). Fix: return undefined then, and have each caller skip the link.
  const url = fillPath('/project/:projectId/forms/:resourceId', {
    projectId: String(projectId),
    resourceId: String(resourceId),
  });
  const searchParams = new URLSearchParams('');

  searchParams.set('projectId', projectId);
  if (contractId) {
    searchParams.set('contractId', contractId);
  } 
  if (resourceId) {
    searchParams.set('resourceId', resourceId);
  }
  if (resourceType) {
    searchParams.set('resourceType', resourceType);
  }
  return `${url}?${searchParams.toString()}`;
};